import React, { useContext, useState } from "react"

import { useQuery } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";

import { Link, matchPath, Route, Routes, useNavigate } from "react-router-dom";
import { Button, Col, Grid, notification } from "antd";
import { Form, Input, Popconfirm, Row, Select } from "antd";
import { Divider, Skeleton } from "antd";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";

import { useRelativePath } from "../../../Hooks/useNextRelativePath";
import { useWatchParameterOnRoutes } from "../../../Hooks/useGetParameterOnRoutes";

import { Conjunto } from "../../../Components/Conjunto";
import { EscolaService } from "../../../Services/Domain/Pessoa/EscolaService";
import { Ordenar } from "../../../Utils/Ordenacao";
import { Permissoes } from "../../../Constants/Permissoes";
import { PropriedadesDeNotificacao } from "../../../Utils/Notificacoes";
import { PropriedadesDoPopConfirm } from "../../../Defaults";
import { ResponsiveAntdTable } from "../../../Components/ResponsiveAntdTable";
import { Restrito } from "../../../Components/Restrito";
import { SessaoContext } from "../../../Contexts/SessaoContext";
import { TypeEscola } from "../../../Models/Domain/Pessoa/Escola";
import { TypeUsuario } from "../../../Models/Usuario";
import { useRedirection } from "../../../Hooks/useRedirection";

import { Formatacoes, Sanitizacoes } from "../../../Utils/Formatacao";

import * as Tutoriais from "./escola.tutorial"
import { InputComReactIMask } from "../../../Components/InputComReactIMask";
import { CadastroDeEscola } from "./Componentes/CadastroDeEscola";

export type TypeDadosDeAcesso = {
    usuario: TypeUsuario["usuario"],
    senha: TypeUsuario["senha"],
}

const ListagemDeEscolas: React.FC<{
    aoAdicionar?: () => void,
    aoEditar?: (escola: TypeEscola) => any,

    hrefDoBotaoAdicionar?: string,
    hrefDoBotaoEditar?: (escola: TypeEscola) => string | void
}> = (props) => {

    const permissoes = useContext(SessaoContext)?.sessao?.usuario?.permissoes.map((p) => p.nome);
    const tamanhoDaTela = Grid.useBreakpoint();
    const usuario = useContext(SessaoContext)?.sessao?.usuario;

    interface IFiltro {
        nome?: string,
        nomeSocial?: string,
        CNPJ?: string,
        ativo?: boolean
    }

    const [filtro, setFiltro] = useState<IFiltro>({
        ativo: true
    })

    const escolasDisponiveis = useQuery({
        queryKey: ["listarEscolas", filtro],
        queryFn: () => new EscolaService().listar({ pessoa: { nome: filtro.nome, nomeSocial: filtro.nomeSocial, cnpj: filtro.CNPJ }, ativo: filtro.ativo }),
        gcTime: 0,
        enabled: true,
    });

    const onFinish = (valores: IFiltro) => {
        setFiltro({
            ...valores,
            CNPJ: valores.CNPJ && Formatacoes.sanitizar(Sanitizacoes.CNPJ, valores.CNPJ)
        })
    };

    const adicionarAberto = !!usuario?.permissoes.map((p) => p.nome).includes(Permissoes.CriarEscola);
    const [chavesDasLinhasExpandidas, setChavesDasLinhasExpandidas] = useState<React.Key[]>([])

    return (
        <React.Fragment>
            <Form onFinish={onFinish} autoComplete="off" layout="vertical">
                <Row gutter={16} id={Tutoriais.IDs.formularioDeFiltro}>
                    <Col xs={24} xl={6} id={Tutoriais.IDs.filtroDeRazaoSocial}>
                        <Form.Item<IFiltro> name={"nome"} label="Razão Social">
                            <Input placeholder="Todos" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={6} id={Tutoriais.IDs.filtroDeNomeFantasia}>
                        <Form.Item<IFiltro> name="nomeSocial" label="Nome Fantasia">
                            <Input placeholder="Todos" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={4} id={Tutoriais.IDs.filtroDeCNPJ}>
                        <Form.Item<IFiltro> name="CNPJ" label="CNPJ">
                            <InputComReactIMask maskOptions={{ mask: "00.000.000/0000-00" }} />
                        </Form.Item>
                    </Col>

                    <Col xs={24} xl={3} id={Tutoriais.IDs.filtroDeAtivo}>
                        <Form.Item<IFiltro> name="ativo" label="Ativo" initialValue={true}>
                            <Select
                                allowClear
                                placeholder={"Todos"}
                                options={[
                                    { value: true, label: "Ativo" },
                                    { value: false, label: "Inativo" },
                                ]}
                            />
                        </Form.Item>
                    </Col>

                    <Col
                        xs={adicionarAberto ? 12 : 24}
                        xl={adicionarAberto ? 2 : 5}
                        id={Tutoriais.IDs.botaoListar}
                        style={{ alignItems: "end", display: "flex" }}
                    >
                        <Form.Item style={{ width: "100%" }}>
                            <Button type="primary" htmlType="submit" disabled={escolasDisponiveis.isFetching} style={{ width: "100%" }}>
                                Listar
                            </Button>
                        </Form.Item>
                    </Col>

                    <Restrito aberto={adicionarAberto}>
                        <Col xs={12} xl={3} id={Tutoriais.IDs.botaoAdicionar} style={{ alignItems: "end", display: "flex" }}>
                            <Form.Item style={{ width: "100%" }}>
                                <Link
                                    to={props.hrefDoBotaoAdicionar ?? "#"}
                                    onClick={(e) => {
                                        !props.hrefDoBotaoAdicionar && e.preventDefault();
                                    }}
                                >
                                    <Button
                                        type="primary"
                                        onClick={() => {
                                            props.aoAdicionar?.();
                                        }}
                                        style={{ width: "100%" }}
                                    >
                                        Adicionar
                                    </Button>
                                </Link>
                            </Form.Item>
                        </Col>
                    </Restrito>
                </Row>
            </Form>

            <Row>
                <Col xs={24}>
                    <ResponsiveAntdTable<TypeEscola>
                        bordered
                        locale={{ emptyText: "Nenhuma escola encontrada." }}
                        dataSource={escolasDisponiveis.data?.data}
                        loading={escolasDisponiveis.isFetching}
                        rowKey={(k) => k.codigo!}
                        addTitleOn="lg"
                        expandable={{
                            showExpandColumn: false,
                            expandedRowRender: (record) => <div>{JSON.stringify(record)}</div>,
                            rowExpandable: () => true,
                            expandedRowKeys: chavesDasLinhasExpandidas,
                        }}
                        columns={[
                            {
                                title: "Código",
                                breakpoints: { xs: 12, sm: 12, lg: 2 },
                                dataIndex: "codigo",
                                sorter: (a, b) => Ordenar.Numero(a.codigo, b.codigo),
                                key: "codigo",
                            },
                            {
                                title: "Ativo",
                                dataIndex: ["ativo"],
                                breakpoints: { xs: 12, sm: 12, lg: 2 },
                                key: "ativo",
                                sorter: (a, b) => Ordenar.Booleano(a.ativo, b.ativo),
                                render: (v: boolean) => {
                                    return <>{v ? "Sim" : "Não"}</>;
                                },
                            },
                            {
                                title: "Nome fantasia",
                                breakpoints: { xs: 12, sm: 8, lg: 7 },
                                dataIndex: ["pessoa", "nomeSocial"],
                                sorter: (a, b) => Ordenar.String(a.pessoa?.nomeSocial, b.pessoa?.nomeSocial),
                                key: "nome-social",
                            },
                            {
                                title: "CNPJ",
                                breakpoints: { xs: 12, sm: 8, lg: 5 },
                                dataIndex: ["pessoa", "cnpj"],
                                sorter: (a, b) => Ordenar.String(a.pessoa?.cnpj, b.pessoa?.cnpj),
                                render: (val) => Formatacoes.formatarCNPJ(val),
                                key: "cnpj",
                            },
                            {
                                breakpoints: { xs: 24, sm: 8, lg: 3 },
                                title: "Responsável",
                                key: "responsavel",
                                render: (_, record) => (
                                    <>
                                        {record.pessoa?.emails?.find((e) => e.principal)?.nomeContato ??
                                            record.pessoa?.telefones?.find((e) => e.principal)?.nomeContato ??
                                            "N/A"}
                                    </>
                                ),
                            },
                            {
                                title: "Ações",
                                key: "acoes",
                                noResponsiveTitle: true,
                                breakpoints: { xs: 24, sm: 24, lg: 5 },
                                hidden: [Permissoes.AtualizarEscola, Permissoes.DesativarEscola, Permissoes.AtivarEscola].every(
                                    (p) => !permissoes?.includes(p)
                                ),
                                render: (_, item) => {
                                    return (
                                        <Conjunto
                                            style={{ justifyContent: tamanhoDaTela.sm ? "unset" : "center", display: "flex", flexWrap: "wrap" }}
                                            itens={[
                                                <Button
                                                    type="dashed"
                                                    size="small"
                                                    icon={item.codigo && chavesDasLinhasExpandidas.includes(item.codigo) ? <MinusOutlined /> : <PlusOutlined />}
                                                    onClick={() => {
                                                        if (!item.codigo) return;

                                                        if (chavesDasLinhasExpandidas.includes(item.codigo)) {
                                                            setChavesDasLinhasExpandidas((prev) => prev.filter((v) => v != item.codigo));
                                                        } else {
                                                            setChavesDasLinhasExpandidas((prev) => [...prev, item.codigo!]);
                                                        }
                                                    }}
                                                ></Button>,
                                                ...(() => {
                                                    if (item.ativo) {
                                                        return [
                                                            usuario?.permissoes.map((p) => p.nome).includes(Permissoes.AtualizarEscola) && (
                                                                <Link
                                                                    to={props.hrefDoBotaoEditar?.(item) ?? "#"}
                                                                    onClick={(e) => !props.hrefDoBotaoEditar && e.preventDefault()}
                                                                >
                                                                    <Button
                                                                        className={Tutoriais.Classes.botaoEditar}
                                                                        type="primary"
                                                                        size="small"
                                                                        onClick={() => {
                                                                            props.aoEditar?.(item);
                                                                        }}
                                                                    >
                                                                        Editar
                                                                    </Button>
                                                                </Link>
                                                            ),
                                                            usuario?.permissoes.map((p) => p.nome).includes(Permissoes.DesativarEscola) && (
                                                                <Popconfirm
                                                                    {...PropriedadesDoPopConfirm("desativar")}
                                                                    onConfirm={() => {
                                                                        if (item.codigo == undefined) return;
                                                                        new EscolaService()
                                                                            .desativar(item.codigo)
                                                                            .then(() => {
                                                                                escolasDisponiveis.refetch();
                                                                            })
                                                                            .catch(() => {});
                                                                    }}
                                                                >
                                                                    <Button type="default" danger size="small" id={Tutoriais.Classes.botaoDesativar}>
                                                                        Desativar
                                                                    </Button>
                                                                </Popconfirm>
                                                            ),
                                                        ];
                                                    } else {
                                                        return [
                                                            usuario?.permissoes.map((p) => p.nome).includes(Permissoes.AtivarEscola) && (
                                                                <Popconfirm
                                                                    {...PropriedadesDoPopConfirm("ativar")}
                                                                    onConfirm={() => {
                                                                        if (item.codigo == undefined) return;

                                                                        new EscolaService()
                                                                            .ativar(item.codigo)
                                                                            .then(() => {
                                                                                escolasDisponiveis.refetch();
                                                                            })
                                                                            .catch(() => {});
                                                                    }}
                                                                >
                                                                    <Button type="dashed" size="small">
                                                                        Reativar
                                                                    </Button>
                                                                </Popconfirm>
                                                            ),
                                                        ];
                                                    }
                                                })(),
                                            ]}
                                        />
                                    );
                                },
                            },
                        ]}
                        csv={{
                            enabled: true,
                            filename: "escolas.csv",
                        }}
                    />
                </Col>
            </Row>
        </React.Fragment>
    );
}

export const Escola: React.FC<{}> = (props) => {
    const [escolaEmAlteracao, setEscolaEmAlteracao] = useState<TypeEscola | undefined>(undefined);
    const permissoes = useContext(SessaoContext)?.sessao?.usuario?.permissoes.map((p) => p.nome);

    const navigate = useNavigate();
    const [raiz, subrota] = useRelativePath();

    const rotaDeEdicao = matchPath({ path: "/editar" }, subrota);
    const rotaDeVisualizacao = matchPath({ path: "/detalhes" }, subrota);

    const obterEscola = useMutation({
        mutationFn: (id: number) => new EscolaService().listar({ codigo: id, ativo: true }),
        gcTime: 0,
        onSuccess: (resposta) => {
            const resultados = resposta.data;
            if (resultados.length == 0) {
                notification.open({
                    ...PropriedadesDeNotificacao.Erro("Nenhum registro foi encontrado para o identificador informado")
                })
                navigate(raiz)
            } else {
                setEscolaEmAlteracao(resposta.data.at(0))
            }
        }
    })

    useRedirection([Permissoes.ListarEscolas, Permissoes.CriarEscola, Permissoes.AtualizarEscola, Permissoes.AtivarEscola, Permissoes.DesativarEscola])
    useWatchParameterOnRoutes({
        appliablePaths: [rotaDeEdicao, rotaDeVisualizacao], paramToWatch: "id", filters: [(p) => isNaN(Number(p))],
        onNotAccepted() { navigate(raiz) },
        onExists(param) { obterEscola.mutate(Number(param)); },
    })

    return (
        <>
            <h2 style={{ marginTop: 0 }}>
                {
                    rotaDeVisualizacao ? "Detalhes da Escola" : rotaDeEdicao ? "Edição da Escola" : "Cadastro da Escola"
                }
            </h2>

            <Divider />

            <Routes>
                {
                    permissoes?.includes(Permissoes.ListarEscolas) &&
                    <Route path="*" index element={
                        <ListagemDeEscolas
                            hrefDoBotaoAdicionar={raiz + "/cadastro"}
                            hrefDoBotaoEditar={(item) => {
                                if (item.codigo) {
                                    return raiz + `/editar?id=${item.codigo}`
                                }
                            }}

                            aoEditar={(escola) => {
                                setEscolaEmAlteracao(escola)
                            }}
                        />
                    } />
                }

                {
                    permissoes?.includes(Permissoes.CriarEscola) &&
                    <Route path="/cadastro" element={
                        <CadastroDeEscola
                            aoCancelar={() => { navigate(raiz) }}
                            depoisDeFinalizar ={() => { navigate(raiz) }}
                            privilegio="administrador"
                        />
                    } />
                }

                {
                    permissoes?.includes(Permissoes.AtualizarEscola) &&
                    <Route path="/editar" element={
                        escolaEmAlteracao == undefined ? <Skeleton active /> :
                            <CadastroDeEscola
                                escolaEmAlteracao={escolaEmAlteracao}
                                aoCancelar={() => { navigate(raiz) }}
                                depoisDeFinalizar={() => { navigate(raiz) }}
                                privilegio="administrador"
                            />
                    } />
                }
            </Routes>

        </>
    );
}